import type { RemoteBundleResponse } from '@yimoka/i18n';
import { createI18n } from '@yimoka/i18n/react';
import { useSyncExternalStore } from 'react';
import { Trans as ReactI18nextTrans } from 'react-i18next';

import { ENV } from '@/env';
import { apiExecutor } from '@/http';
import {
  DEFAULT_LANGUAGE,
  getLanguage,
  setLanguage,
  setLoadingLang,
  subscribeLanguage,
  SUPPORT_LANGUAGES,
} from '@/root';

import enUS from '../../docs/平台i18n配置-en-US.json';
import zhCN from '../../docs/平台i18n配置-zh-CN.json';

const DEFAULT_NAMESPACE = 'translation';

const bundledLocales = {
  'zh-CN': {
    [DEFAULT_NAMESPACE]: zhCN,
  },
  'en-US': {
    [DEFAULT_NAMESPACE]: enUS,
  },
} as const;

function useCurrentLanguage() {
  return useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
}

function toNamespaceBundle(data: unknown): RemoteBundleResponse['data'] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  const record: Record<string, unknown> = { ...data };
  const namespace = record[DEFAULT_NAMESPACE];
  if (namespace && typeof namespace === 'object' && !Array.isArray(namespace)) {
    // 公共类型多保留了一层 NamespaceMap，这里按运行时 backend 需要的 namespace 表适配旧资源。
    return record as RemoteBundleResponse['data'];
  }
  // 公共类型多保留了一层 NamespaceMap，这里按运行时 backend 需要的 namespace 表适配旧资源。
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    [DEFAULT_NAMESPACE]: record,
  } as RemoteBundleResponse['data'];
}

export const {
  i18n,
  I18nRoot,
  t,
  useTranslation,
  useSchemaI18nKey,
} = createI18n({
  bundled: bundledLocales,
  defaultLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORT_LANGUAGES.map(item => item.value),
  useLang: useCurrentLanguage,
  fetchRemote: ENV.loadRemoteLang ? (async (lang) => {
    const response = await apiExecutor({
      url: '/shared/frontend/portal/object/content',
      method: 'GET',
      params: {
        name: 'aspnet-i18n',
      },
      headers: {
        'x-md-global-language': lang,
      },
    });
    return {
      ...response,
      data: toNamespaceBundle(response.data),
    };
  }) : undefined,
  storage: {
    get: key => Promise.resolve(typeof localStorage?.getItem === 'function' ? localStorage.getItem(key) : null),
    set: (key, value) => {
      if (typeof localStorage?.setItem === 'function') {
        localStorage.setItem(key, value);
      }
      return Promise.resolve();
    },
    prefix: 'aspnet:i18n:',
  },
  subscribeAppResume: (onResume) => {
    const handler = () => {
      if (document.visibilityState === 'visible') onResume();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  },
  onLoadingChange: setLoadingLang,
  onLangChange: setLanguage,
  debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
});

export const Trans = ReactI18nextTrans;
